// Заявка на изменение платформы: рассказ агента → что изменилось (файлы, снимки «до / после») → проверка →
// решение владельца. Правка защищённых зон выкатывается только с отдельным подтверждением.
import { Button, Card, Chip, EmptyState, Skeleton, toast, when } from '@autoaff/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import {
  ArrowLeft,
  Check,
  CheckCheck,
  CircleAlert,
  RefreshCw,
  Rocket,
  SearchX,
  ShieldAlert,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { api } from '../api';
import { PageHead } from './common';
import { CR_TONE, type CrRow } from './Dev';

interface CrDetail extends CrRow {
  files: Array<{ path: string; added: number | null; removed: number | null }>;
  protection: {
    verdict: 'ok' | 'confirm' | 'blocked';
    reason: string | null;
    touched: Array<{ path: string; zone: string }>;
  } | null;
  protectedAck: boolean;
  ci: { steps: Array<{ name: string; ok: boolean; ms: number; tail?: string }>; attempts?: number } | null;
  shots: Array<{ name: string; before: string | null; after: string }>;
  ownerComment: string | null;
  threadId: string | null;
  jobs: Array<{
    id: number;
    kind: string;
    status: string;
    error: string | null;
    log: string | null;
    createdAt: string;
  }>;
}

const JOB_RU: Record<string, string> = {
  worktree: 'копия кода',
  ci: 'проверка',
  deploy: 'установка',
  watch: 'наблюдение',
  rollback: 'откат',
  cleanup: 'уборка',
};

const secs = (ms: number) =>
  ms < 60_000 ? `${Math.max(1, Math.round(ms / 1000))} с` : `${Math.round(ms / 60_000)} мин`;

/** Имя снимка → понятная подпись: «laptop-light/leads» → «Лиды · ноутбук». */
function shotLabel(name: string): string {
  const [project, file = ''] = name.split('/');
  return `${file.replace(/-/g, ' ')} · ${project?.startsWith('phone') ? 'телефон' : 'ноутбук'}`;
}

export function DevCrPage() {
  const { id } = useParams({ from: '/dev/$id' });
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['dev-cr', id],
    queryFn: () => api<CrDetail>(`/api/dev/cr/${id}`),
    refetchInterval: 8_000,
  });
  const cr = q.data;
  const [ack, setAck] = useState(false);
  const [asking, setAsking] = useState<'fix' | 'reject' | null>(null);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);

  const act = async (path: string, body: Record<string, unknown>, done: string) => {
    setBusy(true);
    try {
      await api(`/api/dev/cr/${id}/${path}`, body);
      toast(done);
      setAsking(null);
      setComment('');
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['dev-cr', id] }),
        qc.invalidateQueries({ queryKey: ['dev'] }),
      ]);
    } catch (e) {
      toast((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (q.isPending)
    return (
      <div className="pc pc-read">
        <Skeleton h={160} />
      </div>
    );
  if (!cr)
    return (
      <div className="pc pc-read">
        <EmptyState icon={<SearchX />} title="Заявка не найдена">
          <Link to="/dev">Вернуться к «Разработке»</Link>
        </EmptyState>
      </div>
    );
  const confirm = cr.protection?.verdict === 'confirm';
  const added = cr.files.reduce((s, f) => s + (f.added ?? 0), 0);
  const removed = cr.files.reduce((s, f) => s + (f.removed ?? 0), 0);
  return (
    <>
      <PageHead
        title={`Правка #${cr.id}`}
        sub={cr.title}
        actions={
          <Link to="/dev" className="btn">
            <ArrowLeft />
            Все правки
          </Link>
        }
      />
      <div className="pc pc-read">
        <Card>
          <div className="dev-head">
            <Chip tone={CR_TONE[cr.status] ?? 'default'} dot={cr.status === 'ready'}>
              {cr.statusRu}
            </Chip>
            <span className="muted">обновлена {when(cr.updatedAt)}</span>
            {cr.threadId && (
              <Link to="/agent/chats/$threadId" params={{ threadId: cr.threadId }} className="dev-link">
                {cr.status === 'working' ? 'Смотреть, как работает' : 'Как работал агент'}
              </Link>
            )}
          </div>
          <dl className="facts dev-facts">
            <div>
              <dt>Задание</dt>
              <dd>{cr.task}</dd>
            </div>
            {cr.summary && (
              <div>
                <dt>Рассказ агента</dt>
                <dd className="dev-summary">{cr.summary}</dd>
              </div>
            )}
            {cr.ownerComment && (
              <div>
                <dt>Ваш комментарий</dt>
                <dd>{cr.ownerComment}</dd>
              </div>
            )}
          </dl>
          {cr.error && !['deployed', 'watching'].includes(cr.status) && (
            <p className="dev-err">
              <CircleAlert aria-hidden />
              {cr.error}
            </p>
          )}
        </Card>

        {cr.protection && cr.protection.verdict !== 'ok' && (
          <div className={`notice ${cr.protection.verdict === 'blocked' ? 'error' : 'warning'} dev-prot`}>
            <ShieldAlert aria-hidden />
            <div>
              <b>{cr.protection.reason}</b>
              <ul>
                {cr.protection.touched.map((t) => (
                  <li key={t.path}>
                    <span className="mono">{t.path}</span> — {t.zone}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {cr.status === 'ready' && (
          <Card title="Ваше решение">
            {confirm && (
              <label className="dev-ack">
                <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} />
                <span>
                  Понимаю: правка меняет защищённые зоны. Поставить её после проверки и 10 минут наблюдения.
                </span>
              </label>
            )}
            <div className="dev-acts">
              <Button
                variant="primary"
                disabled={busy || (confirm && !ack)}
                onClick={() =>
                  act('approve', { protectedAck: confirm ? ack : undefined }, 'Правка ставится на платформу')
                }
              >
                <Rocket />
                Одобрить и установить
              </Button>
              <Button disabled={busy} onClick={() => setAsking('fix')}>
                <RefreshCw />
                Попросить доработать
              </Button>
              <CloseMerged busy={busy} onClose={() => act('close-merged', {}, CLOSED)} />
              <Button variant="ghost" disabled={busy} onClick={() => setAsking('reject')}>
                <X />
                Отклонить
              </Button>
            </div>
          </Card>
        )}
        {cr.status === 'failed' && (
          <Card title="Что дальше">
            <div className="dev-acts">
              <Button disabled={busy} onClick={() => setAsking('fix')}>
                <RefreshCw />
                Попросить исправить
              </Button>
              <Button disabled={busy} onClick={() => act('recheck', {}, 'Перепроверка поставлена')}>
                <Check />
                Перепроверить
              </Button>
              <CloseMerged busy={busy} onClose={() => act('close-merged', {}, CLOSED)} />
              <Button variant="ghost" disabled={busy} onClick={() => setAsking('reject')}>
                <X />
                Снять заявку
              </Button>
            </div>
          </Card>
        )}
        {cr.status === 'queued' && (
          <Card title="Что дальше">
            <p className="dev-note">
              Сейчас идут три правки — эта начнётся сама, когда одна из них закончится. Ничего делать не
              нужно.
            </p>
            <div className="dev-acts">
              <Button variant="ghost" disabled={busy} onClick={() => setAsking('reject')}>
                <X />
                Снять заявку
              </Button>
            </div>
          </Card>
        )}
        {cr.status === 'rejected' && (
          <Card title="Что дальше">
            <p className="dev-note">
              Если эти изменения всё-таки уже работают в платформе (их установили вместе с другими правками),
              отметьте заявку установленной — агент не будет браться за неё заново.
            </p>
            <div className="dev-acts">
              <CloseMerged busy={busy} onClose={() => act('close-merged', {}, CLOSED)} />
            </div>
          </Card>
        )}
        {asking && (
          <Card title={asking === 'fix' ? 'Что доработать' : 'Почему отклоняете'}>
            <textarea
              className="input dev-comment"
              rows={3}
              value={comment}
              placeholder={
                asking === 'fix'
                  ? 'Например: «Кнопку оставь серой, но сделай шрифт крупнее»'
                  : 'Необязательно'
              }
              aria-label="Комментарий"
              onChange={(e) => setComment(e.target.value)}
            />
            <div className="dev-acts">
              <Button
                variant={asking === 'fix' ? 'primary' : 'danger-soft'}
                disabled={busy || (asking === 'fix' && comment.trim().length < 5)}
                onClick={() =>
                  asking === 'fix'
                    ? act('fix', { comment }, 'Агент дорабатывает правку')
                    : act('reject', { comment: comment || undefined }, 'Заявка отклонена')
                }
              >
                {asking === 'fix' ? 'Отправить агенту' : 'Отклонить'}
              </Button>
              <Button variant="ghost" onClick={() => setAsking(null)}>
                Отмена
              </Button>
            </div>
          </Card>
        )}

        {cr.shots.length > 0 && (
          <Card title="Как изменились экраны" count={cr.shots.length}>
            <div className="dev-shots">
              {cr.shots.map((s) => (
                <figure key={s.name}>
                  <figcaption>{shotLabel(s.name)}</figcaption>
                  <div className="dev-pair">
                    <div>
                      <span className="muted">До</span>
                      {s.before ? (
                        <img src={s.before} alt={`До: ${s.name}`} loading="lazy" />
                      ) : (
                        <em className="muted">нового экрана не было</em>
                      )}
                    </div>
                    <div>
                      <span className="muted">После</span>
                      <img src={s.after} alt={`После: ${s.name}`} loading="lazy" />
                    </div>
                  </div>
                </figure>
              ))}
            </div>
          </Card>
        )}

        {cr.files.length > 0 && (
          <Card title="Изменённые файлы" count={cr.files.length}>
            <p className="muted dev-note">
              +{added} / −{removed} строк
            </p>
            <ul className="dev-files">
              {cr.files.map((f) => (
                <li key={f.path}>
                  <span className="mono">{f.path}</span>
                  <span className="tnum">
                    {f.added === null ? (
                      'файл'
                    ) : (
                      <>
                        <span className="pos">+{f.added}</span> <span className="neg">−{f.removed}</span>
                      </>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        )}

        {cr.ci && (
          <Card title="Независимая проверка">
            <ol className="dev-steps">
              {cr.ci.steps.map((s) => (
                <li key={s.name} data-ok={s.ok}>
                  <span>{s.ok ? <Check aria-label="пройдено" /> : <X aria-label="не пройдено" />}</span>
                  <b>{s.name}</b>
                  <span className="muted tnum">{secs(s.ms)}</span>
                  {s.tail && (
                    <details>
                      <summary>Вывод</summary>
                      <pre className="mono">{s.tail}</pre>
                    </details>
                  )}
                </li>
              ))}
            </ol>
            {cr.ci.attempts && cr.ci.attempts > 1 && (
              <p className="muted dev-note">
                Проверок: {cr.ci.attempts} — между ними агент сам исправлял правку.
              </p>
            )}
          </Card>
        )}

        {cr.jobs.length > 0 && (
          <details className="sp-raw dev-jobs">
            <summary>Журнал установки</summary>
            {cr.jobs.map((j) => (
              <div key={j.id}>
                <b>
                  #{j.id} {JOB_RU[j.kind] ?? j.kind} — {j.status}
                </b>
                {j.error && <span className="dev-err-inline"> {j.error}</span>}
                {j.log && <pre className="mono">{j.log}</pre>}
              </div>
            ))}
          </details>
        )}
      </div>
    </>
  );
}

const CLOSED = 'Заявка отмечена установленной';

/** Изменения заявки уже в платформе (влиты вместе с другими правками): закрыть как «установлена». */
function CloseMerged({ busy, onClose }: { busy: boolean; onClose: () => void }) {
  return (
    <Button
      variant="ghost"
      disabled={busy}
      title="Изменения этой заявки уже работают в платформе — отметить её установленной"
      onClick={onClose}
    >
      <CheckCheck />
      Уже в платформе
    </Button>
  );
}
